/**
 * Main entry point for the Ad Handler library
 * This script loads GPT and creates/extends the global adHandler object
 */

import { GPTLoader } from './gpt-loader';
import { AdManager } from './ad-manager';
import { AdSlot } from './ad-slot';

// Initialize GPT loader and Ad Manager
const gptLoader = GPTLoader.getInstance();
const adManager = new AdManager();

// Check URL for debug parameter and enable debug logging if present
if (typeof window !== 'undefined' && window.location) {
  const urlParams = new URLSearchParams(window.location.search);
  const debugMode = urlParams.get('adhandler_debug') === '1';
  
  if (debugMode) {
    adManager.enableDebugLog = true;
    gptLoader.enableDebugLog = true;
    adManager.log('Debug mode enabled via URL parameter');
  }
}

// Start loading GPT immediately
gptLoader.loadGPT().then(() => {
  adManager.log('GPT library loaded and ready');
}).catch((error) => {
  adManager.log('Failed to load GPT library', error);
});

// Create or extend the global adHandler object
if (typeof window !== 'undefined') {
  // Ensure adHandler exists on window
  window.adHandler = window.adHandler || { cmd: [] };
  window.googletag = window.googletag || { cmd: [] };
  // backward compatibilty to cpg script:
  window.adHandler.service = adManager;


  // runs all commands in adHandler.cmd
  window.adHandler.runCommands = () => {
    const queuedCommands = window.adHandler.cmd || [];
    // replace cmd array with immediate execution, in case more commands are pushed:
    window.adHandler.cmd = {
      push: function(fn: () => void) {
        // Execute immediately now that library is loaded
        fn();
      }
    };    
    queuedCommands.forEach((fn: () => void) => {
      if (typeof fn === 'function') {
        try {
          fn();
        } catch (e) {
          adManager.log('Error executing command in adHandler.cmd', e);
        }
      }
    });
  };
  
  // Expose additional utility functions if needed
  window.adHandler.refreshSlots = () => {
    adManager.refreshSlots();
  };
  
  window.adHandler.clearSlots = () => {
    adManager.clearSlots();
  };
  
  window.adHandler.getSlots = () => {
    return adManager.getSlots();
  };
  
  // Expose GPT loader status
  window.adHandler.isGPTLoaded = () => {
    return gptLoader.isLoaded();
  };
  
  // Expose a method to ensure GPT is ready
  window.adHandler.ready = (callback: () => void) => {
    gptLoader.ready(callback);
  };

  /**
   * Maps dummy ad slots for testing purposes
   * Creates 2 ad divs with dummy targeting parameters and sizes
   */
  window.adHandler.mapping_demo_page = () => {
    const adSlots: AdSlot[] = [
      {
        adUnitPath: "/6355419/Travel/Europe/Germany/Berlin", 
        sizes: [[728, 90], [970, 90]],  // Sizes - leaderboard and large leaderboard
        div: "div-gpt-ad-1"
      },
      {
        adUnitPath: "/6355419/Travel/Europe/Germany/Berlin", 
        sizes: [[300, 250], [336, 280]],
        div: "div-gpt-ad-2"
      }
    ];
    adManager.mapping( adSlots );
  };

  /**
   * Mapping for statista's statistics page
   */
  window.adHandler.mapping_statistics = () => {
    const adSlots: AdSlot[] = [
      // mobile:
      {
        adUnitPath: "/563787768/STT_M_Statistics_Top", 
        sizes: [[320, 50]],
        div: "STT_M_Top",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [] },
          { viewportSize: [300,0], adSizes: [[320, 50]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_M_Statistics_Incontent-1", 
        sizes: [[300, 250]],
        div: "STT_M_Incontent-1",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [] },
          { viewportSize: [300,0], adSizes: [[300, 250]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // desktop:
      {
        adUnitPath: "/563787768/STT_D_Statistics_Top", 
        sizes: [[728, 90]],
        div: "STT_D_Top",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [[728, 90]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_D_Statistics_Incontent-1", 
        sizes: [[300, 250]],
        div: "STT_D_Incontent-1",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [[300, 250]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // very wide desktop additionally:
      {
        adUnitPath: "/563787768/STT_D_Statistics_OffContent-Right", 
        sizes: [[160, 600],[300, 600]],
        div: "STT_D_OffContent-Right",
        adSizeMappings: [          
          { viewportSize: [1800,0], adSizes: [[160, 600], [300, 600]] },
          { viewportSize: [1540,0], adSizes: [[160, 600]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_Statistics_Interstitial", 
        isInterstitial: true
      },
    ];
    adManager.mapping( adSlots );
    adManager.addStickyness(document.getElementsByClassName('advert--sticky-0'));
  };

  /**
   * Mapping for statista's infographics page
   */
  window.adHandler.mapping_infographics = () => {
    const adSlots: AdSlot[] = [
      // mobile:
      {
        adUnitPath: "/563787768/STT_M_Infographics_Top", 
        sizes: [[320, 50]],
        div: "STT_M_Top",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [] },
          { viewportSize: [300,0], adSizes: [[320, 50]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_M_Infographics_Incontent-1", 
        sizes: [[300, 250]],
        div: "STT_M_Incontent-1",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [] },
          { viewportSize: [300,0], adSizes: [[300, 250]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // desktop:
      {
        adUnitPath: "/563787768/STT_D_Infographics_Top", 
        sizes: [[728, 90]],
        div: "STT_D_Top",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [[728, 90]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // very wide desktop additionally:
      {
        adUnitPath: "/563787768/STT_D_Infographics_OffContent-Right", 
        sizes: [[160, 600],[300, 600]],
        div: "STT_D_OffContent-Right",
        adSizeMappings: [          
          { viewportSize: [1800,0], adSizes: [[160, 600], [300, 600]] },
          { viewportSize: [1540,0], adSizes: [[160, 600]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
    ];
    adManager.mapping( adSlots );
    adManager.addStickyness(document.getElementsByClassName('advert--sticky-0'));
  };

  /**
   * Mapping for statista's study page
   */
  window.adHandler.mapping_studies = () => {
    const adSlots: AdSlot[] = [
      // mobile:
      {
        adUnitPath: "/563787768/STT_M_Studies_Top", 
        sizes: [[320, 50]],
        div: "STT_M_Top",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [] },
          { viewportSize: [300,0], adSizes: [[320, 50]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_M_Studies_Incontent-1", 
        sizes: [[300, 250]],
        div: "STT_M_Incontent-1",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [] },
          { viewportSize: [300,0], adSizes: [[300, 250]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // desktop:
      {
        adUnitPath: "/563787768/STT_D_Studies_Top", 
        sizes: [[728, 90]],
        div: "STT_D_Top",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [[728, 90]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_D_Studies_Incontent-1", 
        sizes: [[300, 250]],
        div: "STT_D_Incontent-1",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [[300, 250]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // very wide desktop additionally:
      {
        adUnitPath: "/563787768/STT_D_Studies_OffContent-Right", 
        sizes: [[160, 600],[300, 600]],
        div: "STT_D_OffContent-Right",
        adSizeMappings: [          
          { viewportSize: [1800,0], adSizes: [[160, 600], [300, 600]] },
          { viewportSize: [1540,0], adSizes: [[160, 600]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
    ];
    adManager.mapping( adSlots );
    adManager.addStickyness(document.getElementsByClassName('advert--sticky-0'));
  };

  /**
   * Mapping for statista's topic page
   */
  window.adHandler.mapping_topics = () => {
    const adSlots: AdSlot[] = [
      // mobile:
      {
        adUnitPath: "/563787768/STT_M_Topics_Top", 
        sizes: [[320, 50]],
        div: "STT_M_Top",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [] },
          { viewportSize: [300,0], adSizes: [[320, 50]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_M_Topics_Incontent-1", 
        sizes: [[300, 250]],
        div: "STT_M_Incontent-1",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [] },
          { viewportSize: [300,0], adSizes: [[300, 250]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // desktop:
      {
        adUnitPath: "/563787768/STT_D_Topics_Top", 
        sizes: [[728, 90]],
        div: "STT_D_Top",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [[728, 90]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_D_Topics_Incontent-1", 
        sizes: [[300, 250]],
        div: "STT_D_Incontent-1",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [[300, 250]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // very wide desktop additionally:
      {
        adUnitPath: "/563787768/STT_D_Topics_OffContent-Right", 
        sizes: [[160, 600],[300, 600]],
        div: "STT_D_OffContent-Right",
        adSizeMappings: [          
          { viewportSize: [1800,0], adSizes: [[160, 600], [300, 600]] },
          { viewportSize: [1540,0], adSizes: [[160, 600]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      {
        adUnitPath: "/563787768/STT_Topics_Interstitial", 
        isInterstitial: true
      },
    ];
    adManager.mapping( adSlots );
    adManager.addStickyness(document.getElementsByClassName('advert--sticky-0'));
  };

  /**
   * Mapping for statista's new statistics page (statista 4.0 in 2026)
   */
  window.adHandler.mapping_skellige_statistics_ads = () => {
    const adSlots: AdSlot[] = [
      // mobile and desktop:
      {
        adUnitPath: "/563787768/STT_M_Statistics_Incontent-1", 
        sizes: [[300, 250]],
        div: "STT_M_Statistics_Incontent-1",
        adSizeMappings: [
          { viewportSize: [768,0], adSizes: [[300, 250]] },
          { viewportSize: [300,0], adSizes: [[300, 250]] },
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
      // desktop:
      {
        adUnitPath: "/563787768/STT_D_Statistics_Desktop_Leaderboard", 
        sizes: [[970, 90]],
        div: "STT_D_Statistics_Desktop_Leaderboard",
        adSizeMappings: [
          { viewportSize: [1200,0], adSizes: [[970, 90]] }, // BreakpointForDesktop
          { viewportSize: [0,0], adSizes: [] },
        ]
      },
//      {
//        adUnitPath: "/563787768/STT_Statistics_Interstitial", 
//        isInterstitial: true
//      },
    ];
    adManager.mapping( adSlots );
    adManager.addStickyness(document.getElementsByClassName('advert--sticky-0'));
  };


  adManager.log('Global adHandler object initialized');
  window.adHandler.runCommands(); // execute everything the page has put into the cmd array:
}

// Export for module usage (if needed)
export { GPTLoader, AdManager };
